import { apiClient } from './client';

// Mirrors packages/api/src/modules/outbound/report.ts (the web app keeps its own
// copy of response shapes, as gtm.ts and funnel.ts do).

export const EFFORT_CHANNELS = [
  'linkedinInvites',
  'linkedinMessages',
  'linkedinVoiceNotes',
  'emails',
  'calls',
  'manualTasks',
] as const;

export type EffortChannel = (typeof EFFORT_CHANNELS)[number];

/**
 * The channels the page always shows as columns. Voice notes, calls and manual
 * tasks get a column only in a week where someone used them, so the channel
 * columns always add up to Touches.
 */
export const SHOWN_EFFORT_CHANNELS: readonly EffortChannel[] = ['linkedinInvites', 'linkedinMessages', 'emails'];

export const EFFORT_CHANNEL_LABELS: Record<EffortChannel, string> = {
  linkedinInvites: 'LI invites',
  linkedinMessages: 'LI messages',
  linkedinVoiceNotes: 'Voice notes',
  emails: 'Emails',
  calls: 'Calls',
  manualTasks: 'Manual tasks',
};

export interface RepWeek {
  week: string;
  /** lemlist user id (the person, not the mailbox), `apollo:<id>` for an SDR's dialer, or `unattributed`. */
  rep: string;
  repEmail: string | null;
  repName: string | null;
  effort: Record<EffortChannel, number>;
  totalTouches: number;
  leadsTouched: number;
  profileVisits: number;
  /** Replies received this week — often to touches sent in earlier weeks. */
  replies: number;
  positiveReplies: number;
  /** Distinct people first invited on LinkedIn this week. */
  invitedPeople: number;
  /** Of `invitedPeople`, how many have accepted so far (any time). */
  invitesAccepted: number;
  /** invitesAccepted / invitedPeople; null when nobody was invited. */
  acceptanceRate: number | null;
  /** Real failures — pending-invitation messages are not in here. */
  failures: number;
  /** Messages fired before the invite was accepted (a sequence-design warning). */
  messagesBeforeAccept: number;
}

export interface WeekTotal {
  week: string;
  touches: number;
  /** Distinct people touched by anyone in the week. */
  leadsTouched: number;
  replies: number;
  invitedPeople: number;
  invitesAccepted: number;
  acceptanceRate: number | null;
  failures: number;
  messagesBeforeAccept: number;
}

export interface SequenceWarning {
  week: string;
  campaignId: string | null;
  campaignName: string | null;
  count: number;
}

/** The Outbound tab's groups: SDRs (Masa, Jovana) and AEs (Nadia, Patrick). Walter is in neither. */
export type OutboundGroup = 'sdr' | 'ae';

/** A person on the outbound roster, with every report `rep` key their rows carry. */
export interface SalesRep {
  slug: string;
  name: string;
  email: string;
  group: OutboundGroup | null;
  lemlistUserIds: string[];
  /** lemlist user ids plus `apollo:<id>` for an SDR's dialer. Empty = no tracked account. */
  repKeys: string[];
}

export interface OutboundReport {
  from: string;
  to: string;
  /** Oldest first, ending with the week in progress; never before the first synced activity. */
  weeks: string[];
  currentWeek: string;
  lastCompleteWeek: string;
  earliestActivityAt: string | null;
  rows: RepWeek[];
  weekTotals: WeekTotal[];
  failureReasons: { week: string; reason: string; count: number }[];
  sequenceWarnings: SequenceWarning[];
  /** The whole roster, not narrowed by `group`. */
  reps: SalesRep[];
  group: OutboundGroup | null;
}

export async function getOutboundActivity(weeks: number, group: OutboundGroup | null = null): Promise<OutboundReport> {
  const groupParam = group ? `&group=${group}` : '';
  return apiClient.get<OutboundReport>(`/outbound/activity?weeks=${weeks}${groupParam}`);
}
